import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

import { LocalizationService } from 'app/core/localization/localization.service';
import { CustomDateLocalizedFormats } from 'app/shared/constants/custom-date-localized-formats';
import { DateFormats } from 'app/shared/constants/date-formats';

@Injectable({
  providedIn: 'root'
})
export class DateService {
  public constructor(private readonly localizationService: LocalizationService) {}

  public localeDate(date: Date, localizedFormat: string, localeId: string): string {
    const locale = (localeId || this.localizationService.defaultLocaleId).toLowerCase();
    const formatTemplate = this.getCustomLocalizedFormatIfExist(locale, localizedFormat);

    if (formatTemplate === DateFormats.KH_SHORTEST) {
      return DateTime.fromJSDate(date).setLocale(localeId).toLocaleString(DateTime.DATE_SHORT);
    }

    return this.createDate(date).setLocale(locale).toFormat(formatTemplate);
  }

  public getCustomLocalizedFormatIfExist(locale: string, format: string): DateFormats {
    const localeFormats = CustomDateLocalizedFormats[locale as keyof typeof CustomDateLocalizedFormats];
    return (localeFormats?.[format as keyof typeof localeFormats] ?? format) as DateFormats || DateFormats.LL;
  }

  private createDate(date: Date): DateTime {
    const dateTime = DateTime.fromJSDate(date);
    return dateTime.isValid ? dateTime : DateTime.now();
  }

  /**
   * Converts a UTC timestamp that represents user's local time back to proper local time
   * @param utcTimestamp The UTC timestamp that actually represents user's local time
   * @returns A Date object representing the user's actual local time
   */
  public convertUtcDateToLocalDate(utcTimestamp: Date): Date {
    return DateTime.fromJSDate(utcTimestamp, { zone: 'utc' })
      .setZone('local', { keepLocalTime: true })
      .toJSDate();
  }
}
